import type { API } from 'chessboard/api/config/types';

/**
 * Create the overrideAPI method.
 */
export function createOverrideAPI(context: API.Context) {
  const apiOverrides: { [key: string]: MethodOverride } = {};

  /**
   * Overrides api methods.
   */
  return function overrideAPI(
    pluginName: string,
    pluginOverrides: Record<string, PluginOverride>,
  ): () => void {
    const overrides = Object.entries(pluginOverrides);

    overrides.forEach(([methodName, pluginOverride]) => {
      let method = apiOverrides[methodName];

      if (!method) {
        apiOverrides[methodName] = {
          methodName,
          original: context.api[methodName],
          current: context.api[methodName],
          before: [],
          after: [],
          instead: [],
          extensions: {},
        };
        method = apiOverrides[methodName];
        context.api[methodName] = withOverrides(methodName);
      }

      const addOverride = (override: Override) => {
        method[override.run].push({
          id: pluginName,
          ...override,
          handler: override.handler(context),
        });
        method[override.run].sort((a, b) => (a.order || 0) - (b.order || 0));
      };

      if (Array.isArray(pluginOverride)) {
        pluginOverride.forEach((override) => {
          addOverride(override);
        });
      } else if (typeof pluginOverride === 'object') {
        addOverride(pluginOverride);
      } else if (typeof pluginOverride === 'function') {
        // legacy override
        method.extensions[pluginName] = pluginOverride;
        method.current = pluginOverride(reformContext(method));
      }
    });

    return function destroyOverrides() {
      overrides.forEach(([methodName]) => {
        const method = apiOverrides[methodName];

        delete method.extensions[pluginName];
        const nextOverride = Object.values(method.extensions)[0];
        method.current = nextOverride
          ? nextOverride(reformContext(method))
          : (method.original as API.APIFactory);
        // code above is to support legacy overrides

        ['before', 'after', 'instead'].forEach((type) => {
          method[type] = method[type].filter((override) => override.id !== pluginName);
        });
      });
    };
  };

  /**
   * Factory fn to run overrides for a given method.
   */
  function withOverrides(methodName) {
    const method = apiOverrides[methodName];

    const getOverrides = (type: RunType, ...args) =>
      method[type].filter((override) => {
        if (!override.conditions) {
          return true;
        }

        if (Array.isArray(override.conditions)) {
          return override.conditions.every((condition) => Boolean(condition(context, ...args)));
        }

        return Boolean(override.conditions(context, ...args));
      });

    return (...args: []) => {
      const runBefore = getOverrides('before', ...args);
      const runInstead = getOverrides('instead', ...args)[0]?.handler;

      const reducedArgs = runBefore.reduce((newArgs, override) => {
        const input = override.handler(...newArgs);
        if (override.changeArgs) {
          return input;
        } else {
          return newArgs;
        }
      }, args);

      let result;
      if (runInstead) {
        result = runInstead(...reducedArgs);
      } else {
        result = method.current(...reducedArgs);
      }

      const runAfter = getOverrides('after', ...reducedArgs, result);

      const final = runAfter.reduce(
        (newResult, override) => override.handler(newResult, ...reducedArgs),
        result,
      );

      return final;
    };
  }

  /**
   * For legacy overrides replace original API method
   */
  function reformContext(method) {
    return {
      ...context,
      api: {
        ...context.api,
        [method.methodName]: method.original,
      },
    };
  }
}

export type PluginOverride = Override | Override[] | API.APIFactory;
export type Predicate = (context: API.Context, ...args: any) => boolean;
export type RunType = 'instead' | 'before' | 'after';

export interface Override {
  id?: string;
  conditions?: Predicate | Predicate[];
  handler: Function;
  run: RunType;
  order?: number;
  changeArgs?: boolean;
}

interface MethodOverride {
  methodName: string;
  original: Function;
  current: Function;
  extensions: { [key: string]: API.APIFactory };
  before: Override[];
  after: Override[];
  instead: Override[];
}
